"use client";

import { User, Mail, Building, Briefcase, Edit, Info } from "lucide-react";
import "./ProfilePage.css";
import BackButton from "../shared/backButton";
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { getUserProfile } from "../../services/sharedService";
import { useTranslation } from "react-i18next";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { updateConsoleUserProfileApi } from "../../services/settingsService";
import { sendVerificationEmailApi } from "../../services/authService";
import MessageManager from "../layout/Message";
import { useNavigate } from "react-router-dom";

const ProfileFieldTooltip = ({ title, children, className = "" }) => {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = Math.min(300, window.innerWidth - 32);
    const margin = 16;

    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    if (left < margin) left = margin;
    if (left + tooltipWidth > window.innerWidth - margin) {
      left = window.innerWidth - tooltipWidth - margin;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < 190 && rect.top > 190;

    setCoords({
      left,
      width: tooltipWidth,
      top: openAbove ? rect.top - 10 : rect.bottom + 10,
      placement: openAbove ? "top" : "bottom",
    });
  };

  useEffect(() => {
    if (!open) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="pp-field-tooltip-trigger"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-label={title}
      >
        <Info size={15} />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            className={`pp-field-tooltip-popover ${coords.placement} ${className}`}
            style={{
              left: coords.left,
              width: coords.width,
              top: coords.placement === "bottom" ? coords.top : "auto",
              bottom:
                coords.placement === "top"
                  ? window.innerHeight - coords.top
                  : "auto",
            }}
            role="tooltip"
          >
            {title && <div className="pp-field-tooltip-title">{title}</div>}
            <div className="pp-field-tooltip-body">{children}</div>
          </div>,
          document.body,
        )}
    </>
  );
};

const ProfilePage = () => {
  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [organization, setOrganization] = useState("");
  const { t } = useTranslation();
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);

  const roleLegendItems = [
    {
      title: "Super Admin",
      description: "Full system control and admin management",
    },
    {
      title: "Admin",
      description: "Day-to-day organization and department management",
    },
    {
      title: "Manager",
      description:
        "Team-focused; handles hiring, talent mobility, and leadership potential",
    },
    {
      title: "Brand Manager",
      description: "Focused on sub brands for business and white labels",
    },
    {
      title: "Guest",
      description:
        "View-only access with specific capabilities for mobility recommendations",
    },
  ];

  useEffect(() => {
    fetchProfiles();
  }, []);
  const [organizationsDataList, setOrganizationsDataList] = useState([]);
  // Function to fetch user profile data
  const fetchProfiles = async () => {
    try {
      let mainTenantId = sessionStorage.getItem("mainTenantId");
      const data = await getUserProfile(mainTenantId);
      // sessionStorage.setItem("role", data.data.role[0]);

      setProfileData(data.data);
      setEditedData(data.data);
      setOrganizationsDataList(data.data.organizations);
      const existingTenantId = sessionStorage.getItem("tenantId");
      const matchingOrg = data.data.organizations?.find(
        (org) =>
          org?.tenantId !== undefined &&
          String(org.tenantId) === String(existingTenantId),
      );
      setOrganization(
        matchingOrg?.tenantId || data.data.organizations[0]?.tenantId || "",
      );
      console.log(data, "Profile data fetched successfully");
    } catch (error) {
      handleApiError(error);
    }
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message ||
          "Please upgrade your plan to continue using this feature.",
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    // Here you would typically make an API call to save the changes
    setProfileData(editedData);
    setIsEditing(false);
    console.log("Saving changes:", editedData);
    try {
      let obj = {
        email: editedData.email,
        name: editedData.name,
        language: sessionStorage.getItem("i18nextLng") || "en",
        defaultOrg: organization,
      };
      const response = await updateConsoleUserProfileApi(obj);
      if (response.code === 200) {
        fetchProfiles(); // Refresh profile data after saving

        MessageManager.showToast("success", t("profilePage.profileUpdated"));
        const existingTenantId = sessionStorage.getItem("tenantId");
        const matchingOrg = response.data.organizations?.find(
          (org) =>
            org?.tenantId !== undefined &&
            String(org.tenantId) === String(existingTenantId),
        );
        const activeOrg = matchingOrg || response.data.organizations?.[0];

        sessionStorage.setItem("tenantId", activeOrg?.tenantId || "");
        sessionStorage.setItem("organization", activeOrg?.organization || "");
        sessionStorage.setItem("orgCode", activeOrg?.orgCode || "");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const handleCancel = () => {
    setEditedData(profileData);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    console.log("Input changed:", field, value);
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOrganizationChange = (value) => {
    console.log("Organization changed:", value);
    // Assuming value is an object with organization and tenantId properties
    // setEditedData((prev) => ({
    //   ...prev,
    //   organizations: [
    //     {
    //       ...prev.organizations?.[0],
    //       organization: value,
    //     },
    //   ],
    // }));
    console.log("Selected organization:", value);
    setOrganization(value);
  };

  const handleVerifyEmail = async () => {
    if (!profileData?.email) {
      MessageManager.showToast("error", "Email address not found.");
      return;
    }

    setIsVerifyingEmail(true);

    try {
      const response = await sendVerificationEmailApi(profileData.email);

      if (response?.code === 200) {
        MessageManager.showToast(
          "success",
          "Verification email sent successfully!",
        );
      } else if (response?.code === 429) {
        MessageManager.showToast(
          "error",
          "Maximum verification email attempts reached. Please try again after 24 hours.",
        );
      } else {
        MessageManager.showToast("error", "Failed to send verification email.");
      }
    } catch (error) {
      console.error("Error sending verification email:", error);

      if (
        error?.response?.status === 429 ||
        error?.status === 429 ||
        error?.code === 429
      ) {
        MessageManager.showToast(
          "error",
          "Limit reached for the day. Unable to send verification email",
        );
        return;
      }

      MessageManager.showToast("error", "Failed to send verification email.");
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  console.log("RENDERED profileData:", profileData);

  // --- Subscription values come from organizations[] (API added keys) ---
  const selectedTenantId = organization;

  const activeOrg =
    organizationsDataList?.find((o) => o?.tenantId === selectedTenantId) ||
    organizationsDataList?.[0] ||
    null;

  const subscriptionStatus = activeOrg?.subscriptionStatus || "Inactive";
  const startDate = activeOrg?.startDate || null;
  const endDate = activeOrg?.endDate || null;

  const formatDate = (value) => {
    if (!value) return t("common.notAvailable");
    const d = new Date(value);
    return isNaN(d.getTime())
      ? t("common.notAvailable")
      : d.toLocaleDateString();
  };

  // Email verification status (mock - replace with actual API data)
  const isEmailVerified =
    profileData?.verifiedEmail === true || profileData?.verified_email === true;

  const emailVerificationStatus = isEmailVerified
    ? "verified"
    : profileData?.email_verification_status || "unverified";

  const normalizeProfileRoleName = (value) => {
    const roleValue = Array.isArray(value) ? value[0] : value;

    return String(roleValue || "")
      .toLowerCase()
      .replace(/[-_]/g, " ")
      .trim();
  };

  const assignedRoleLegend = roleLegendItems.find(
    (item) =>
      normalizeProfileRoleName(item.title) ===
      normalizeProfileRoleName(profileData?.role),
  ) || {
    title: Array.isArray(profileData?.role)
      ? profileData?.role?.[0]
      : profileData?.role || t("common.notAvailable"),
    description: "No description available for this role.",
  };

  if (profileData) {
    return (
      <div className="pp-profile-page">
        {/* <BackButton /> */}
        <div className="pp-back-wrap">
          <BackButton />
        </div>

        <main className="pp-profile-main">
          <div className="pp-container">
            {/* Email verification banner moved to the global layout banner. */}
            {/*
            {!isEmailVerified && emailVerificationStatus !== "locked" && (
              <div className="ev-top-banner ev-banner-unverified">
                <div className="ev-banner-content">
                  <div className="ev-banner-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                  <div className="ev-banner-text">
                    <p className="ev-banner-message">
                      Your email is not verified. You will not receive any
                      notifications until you verify your email.
                    </p>
                  </div>
                  <button
                    className="ev-banner-button"
                    onClick={handleVerifyEmail}
                    disabled={isVerifyingEmail}
                  >
                    {isVerifyingEmail ? (
                      <>
                        <span className="ev-banner-spinner"></span>
                        Sending...
                      </>
                    ) : (
                      "Verify Email"
                    )}
                  </button>
                </div>
              </div>
            )}
            */}

            <div className="pp-page-header">
              {/* Column 1: icon + title/desc */}
              <div className="pp-header-left">
                <div className="pp-header-icon">
                  <User size={32} />
                </div>

                <div className="pp-header-titles">
                  <h1>{t("profilePage.pageTitle")}</h1>
                  <p>{t("profilePage.pageDescription")}</p>
                </div>
              </div>

              {/* Separator */}
              <div className="pp-header-separator" />

              {/* Column 2: subscription */}
              <div className="pp-header-right">
                <div className="pp-sub-badge">
                  <span className="pp-sub-text" style={{ fontWeight: "500" }}>
                    PLAN :{" "}
                  </span>
                  <span className="pp-sub-dot" />
                  <span className="pp-sub-text">
                    {subscriptionStatus || t("common.notAvailable")}
                  </span>
                </div>

                <div className="pp-sub-dates">
                  <div className="pp-sub-date">
                    <span className="pp-sub-label">Start</span>
                    <span className="pp-sub-value">
                      {formatDate(startDate)}
                    </span>
                  </div>
                  <div className="pp-sub-date">
                    <span className="pp-sub-label">End</span>
                    <span className="pp-sub-value">{formatDate(endDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="pp-profile-content">
              <div className="pp-profile-card">
                <div className="pp-card-header">
                  <button
                    className="pp-edit-button"
                    onClick={handleEdit}
                    disabled={isEditing}
                  >
                    <Edit size={16} />
                  </button>
                </div>

                <div className="pp-profile-details">
                  <div className="pp-detail-item">
                    <User size={16} />
                    <div className="pp-detail-content">
                      <div className="pp-role-info-label-row">
                        <label>{t("profilePage.fullName")}</label>

                        <ProfileFieldTooltip
                          title="Full Name"
                          className="pp-name-field-tooltip"
                        >
                          This name is linked to your profile and may be visible
                          across your organization.
                        </ProfileFieldTooltip>
                      </div>
                      {isEditing ? (
                        <input
                          disabled
                          type="text"
                          value={editedData?.name || ""}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          className="pp-edit-input"
                        />
                      ) : (
                        <span>{profileData?.name}</span>
                      )}
                    </div>
                  </div>

                  <div className="pp-detail-item">
                    <Mail size={16} />
                    <div className="pp-detail-content">
                      <label>{t("profilePage.email")}</label>
                      <div className="ev-email-input-wrapper">
                        {isEditing ? (
                          <input
                            type="email"
                            value={editedData?.email || ""}
                            disabled
                            readOnly
                            className="pp-edit-input"
                          />
                        ) : (
                          <span>{profileData?.email}</span>
                        )}

                        {/* Email Status Badge */}
                        {isEmailVerified ? (
                          <div className="ev-email-badge ev-badge-verified">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>Verified</span>
                          </div>
                        ) : emailVerificationStatus === "locked" ? (
                          <div className="ev-email-badge ev-badge-locked">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect
                                x="3"
                                y="11"
                                width="18"
                                height="11"
                                rx="2"
                                ry="2"
                              />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            <span>Locked</span>
                          </div>
                        ) : (
                          <div className="ev-email-badge ev-badge-unverified">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>Unverified</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pp-detail-item ">
                    <Building size={16} />
                    <div className="pp-detail-content">
                      <label>{t("profilePage.DefaultOrganization")}</label>
                      {isEditing ? (
                        // <input
                        //   disabled
                        //   type="text"
                        //   value={
                        //     editedData?.organizations?.[0]?.organization || ""
                        //   }
                        //   onChange={(e) =>
                        //     handleOrganizationChange(e.target.value)
                        //   }
                        //   className="pp-edit-input"
                        // />
                        <select
                          style={{
                            padding: "12px 16px",
                            border: "1px solid var(--border-color)",
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "border-color 0.2s ease",
                            width: "calc(100% - 0px)",
                          }}
                          className="pp-edit-input"
                          value={organization || ""}
                          onChange={(e) =>
                            handleOrganizationChange(e.target.value)
                          }
                        >
                          {organizationsDataList.map((org) => (
                            <option key={org.orgCode} value={org.tenantId}>
                              {org.organization}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {profileData?.organizations[0]?.organization ||
                            t("common.notAvailable")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pp-detail-item">
                    <Briefcase size={16} />
                    <div className="pp-detail-content">
                      <div className="pp-role-info-label-row">
                        <label>{t("profilePage.role")}</label>

                        <ProfileFieldTooltip
                          title="Current Role"
                          className="pp-role-field-tooltip"
                        >
                          <strong>{assignedRoleLegend.title}</strong>
                          <span>{assignedRoleLegend.description}</span>
                        </ProfileFieldTooltip>
                      </div>
                      {isEditing ? (
                        <input
                          disabled
                          type="text"
                          value={editedData?.role || ""}
                          onChange={(e) =>
                            handleInputChange("role", e.target.value)
                          }
                          className="pp-edit-input"
                        />
                      ) : (
                        <span>
                          {profileData?.role
                            ? profileData.role
                            : t("common.notAvailable")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pp-detail-item">
                    <Building size={16} />
                    <div className="pp-detail-content">
                      <label>{t("profilePage.department")}</label>
                      {isEditing ? (
                        <input
                          disabled
                          type="text"
                          value={editedData?.department || ""}
                          onChange={(e) =>
                            handleInputChange("department", e.target.value)
                          }
                          className="pp-edit-input"
                        />
                      ) : (
                        <span>
                          {profileData?.department
                            ? profileData.department
                            : t("common.notAvailable")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {isEditing && (
                  <div className="pp-action-buttons">
                    <button className="pp-cancel-button" onClick={handleCancel}>
                      {t("common.cancel")}
                    </button>
                    <button className="pp-save-button" onClick={handleSave}>
                      {t("profilePage.saveChanges")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="pp-profile-page">
        <BackButton />

        <main className="pp-profile-main">
          <div className="pp-container">
            <div className="pp-page-header">
              <div className="pp-header-icon">
                <User size={32} />
              </div>
              <h1>{t("profilePage.pageTitle")}</h1>
            </div>

            <div className="pp-profile-content">
              <div className="pp-profile-card">
                <div className="pp-profile-details">
                  {[...Array(5)].map((_, idx) => (
                    <div className="pp-detail-item" key={idx}>
                      <div className="pp-skeleton-icon" />
                      <div className="pp-detail-content">
                        <div className="pp-skeleton-label" />
                        <div className="pp-skeleton-text" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

        <SubscriptionExpiredModal
          show={showSubscriptionExpired}
          message={subscriptionMessage}
          onRedirect={handleSubscriptionRedirect}
        />
      </div>
    );
  }
};

export default ProfilePage;
